/**
 * 온톨로지 레이어 매핑 — Graph 탭의 레이어 필터 UI 에 사용.
 *
 * 원본: `src/dashboard/src/components/graph/graph-layers.ts` 에서 shared 로 이전.
 * pion-knowledge 의 새 kind 추가 시 여기에 동반 등록. 미등록 kind 는 필터에서 제외되지 않는다.
 */

export const LAYERS = ["domain", "component", "resource", "context", "detail"] as const
export type Layer = (typeof LAYERS)[number]

/** kind → layer 매핑 */
export const KIND_TO_LAYER: Record<string, Layer> = {
	// domain — 비즈니스 단위 (core-org + engineering 서비스/리포 + product + sales/marketing 상위)
	org: "domain",
	team: "domain",
	person: "domain",
	persona: "domain",
	role: "domain",
	repository: "domain",
	service: "domain",
	product: "domain",
	feature: "domain",
	product_goal: "domain",
	product_metric: "domain",
	milestone: "domain",
	jira_issue: "domain",
	jira_epic: "domain",
	jira_sprint: "domain",
	roadmap: "domain",
	external_api: "domain",
	saas_service: "domain",
	vendor: "domain",
	environment: "domain",
	// sales — GTM 상위 단위는 domain 레이어
	account: "domain",
	lead: "domain",
	opportunity: "domain",
	deal: "domain",
	pipeline_stage: "domain",
	customer_segment: "domain",
	quota: "domain",
	sales_metric: "domain",
	// marketing — 캠페인 상위도 domain
	campaign: "domain",
	audience: "domain",
	brand: "domain",
	funnel: "domain",
	experiment: "domain",
	marketing_metric: "domain",
	// design — 디자인 시스템 자체는 domain
	design_system: "domain",
	// decisions — 의사결정은 domain
	decision: "domain",
	// literature 팩 — 인물/지역/학교/기숙사는 domain
	character: "domain",
	location: "domain",
	school: "domain",
	house: "domain",
	// place 계열 — 장소/행정구역은 환경과 동일하게 domain
	place: "domain",
	administrative_area: "domain",
	// actor — AI Agent 는 person/team 과 같은 domain 행위자
	ai_agent: "domain",
	// structure(metric) — 스코어카드는 product_metric 처럼 상위 domain
	scorecard: "domain",
	// component — 빌딩블록 + 주요 design 구성요소
	module: "component",
	compute_cluster: "component",
	compute_service: "component",
	serverless_function: "component",
	relational_db: "component",
	document_db: "component",
	cache: "component",
	object_store: "component",
	load_balancer: "component",
	cdn: "component",
	api_gateway: "component",
	message_queue: "component",
	container_registry: "component",
	design_component: "component",
	ux_pattern: "component",
	// resource — 세부 인프라 설정
	secret_store: "resource",
	identity_provider: "resource",
	target_group: "resource",
	vpc: "resource",
	security_group: "resource",
	route53_zone: "resource",
	sns_topic: "resource",
	parameter_store: "resource",
	db_schema: "resource",
	design_token: "resource",
	// context — 부가 아티팩트 + 커뮤니케이션 + 증거 + 스펙 + 콘텐츠
	view: "context",
	user_journey: "context",
	library: "context",
	package: "context",
	workflow: "context",
	api_endpoint: "context",
	dataset: "context",
	data_pipeline: "context",
	release: "context",
	deployment: "context",
	channel: "context",
	page: "context",
	document: "context",
	thread: "context",
	specification: "context",
	sales_meeting: "context",
	contract: "context",
	marketing_asset: "context",
	marketing_channel: "context",
	content_piece: "context",
	landing_page: "context",
	brand_asset: "context",
	design_review: "context",
	design_file: "context",
	wireframe: "context",
	mockup: "context",
	incident: "context",
	runbook: "context",
	knowhow: "context",
	evidence: "context",
	// literature 팩 — 마법 아이템/생물은 context/detail
	magical_object: "context",
	creature: "context",
	// product/event/content/structure 추가 kind — 부가 아티팩트/이벤트/데이터/PR 는 context
	artifact: "context",
	event: "context",
	change_record: "context",
	performance_anomaly: "context",
	security_finding: "context",
	context_note: "context",
	data_catalog: "context",
	data_feed: "context",
	source_module: "context",
	pull_request: "context",
	// detail — 최소 단위 (소스 코드 등)
	source_file: "detail",
	code_function: "detail",
	code_class: "detail",
	code_type: "detail",
	test_suite: "detail",
	// structure(metric) — 원자적 측정값/평점은 최소 단위 detail
	quantitative_value: "detail",
	rating: "detail"
}

export const LAYER_LABELS: Record<Layer, string> = {
	domain: "도메인",
	component: "컴포넌트",
	resource: "리소스",
	context: "컨텍스트",
	detail: "디테일"
}

export const LAYER_COLORS: Record<Layer, string> = {
	domain: "#3B82F6",
	component: "#F59E0B",
	resource: "#8B5CF6",
	context: "#10B981",
	detail: "#EF4444"
}
